import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { flexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './button.js';
import { Checkbox } from './checkbox.js';
import { cn } from './cn.js';
import { EmptyState } from './empty-state.js';
import { SimpleSelect } from './select.js';
import { Skeleton } from './skeleton.js';
/** The expand chevron, for a cell that owns it (`expandable.toggleColumn: false`). */
export function DataTableExpandToggle({ row, children }) {
    const { t } = useTranslation();
    if (!row.getCanExpand())
        return _jsx(_Fragment, { children: children });
    const open = row.getIsExpanded();
    return (_jsxs("button", { type: "button", "aria-expanded": open, "aria-label": children ? undefined : open ? t('kit.collapseRow') : t('kit.expandRow'), className: "-ml-1 inline-flex min-w-0 items-center gap-1 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring", onClick: (event) => {
            event.stopPropagation();
            row.toggleExpanded();
        }, children: [_jsx(ChevronRight, { "aria-hidden": true, className: cn('size-4 shrink-0 text-muted-foreground transition-transform duration-(--duration-fast)', open && 'rotate-90') }), children] }));
}
const DEFAULT_PAGE_SIZES = [25, 50, 100];
function alignClass(align) {
    return align === 'right' ? 'text-right tnum' : align === 'center' ? 'text-center' : 'text-left';
}
/**
 * Where each visible column goes in the phone layout (see `ColumnMeta.phone`):
 * the first data column is primary, display columns before it (selection,
 * expand, a drag handle) lead line one, `actions` trails, the rest share line two.
 */
export function phoneRoles(columns) {
    const roles = new Map();
    let primaryTaken = columns.some((column) => column.phone === 'primary');
    for (const { id, phone, accessor } of columns) {
        if (id === '__select' || id === '__expand')
            roles.set(id, 'leading');
        else if (phone)
            roles.set(id, phone);
        else if (id === 'actions')
            roles.set(id, 'trailing');
        else if (!primaryTaken && !accessor)
            roles.set(id, 'leading');
        else if (!primaryTaken) {
            roles.set(id, 'primary');
            primaryTaken = true;
        }
        else
            roles.set(id, 'secondary');
    }
    return roles;
}
/**
 * Phone classes. Below `md` the table stops being a grid: every row is a flex
 * list item whose `::before` is a zero-height full-width break, so the
 * leading, primary and trailing cells (order 0) make line one and the
 * secondary cells (order 2) wrap onto line two. The DOM stays a table, so
 * roles, labels and tests are the same at every width.
 */
const PHONE_CELL = {
    leading: 'max-md:order-0 max-md:shrink-0',
    primary: 'max-md:order-0 max-md:min-w-0 max-md:flex-1 max-md:truncate max-md:font-medium max-md:text-foreground',
    trailing: 'max-md:order-0 max-md:ml-auto max-md:shrink-0',
    secondary: 'max-md:order-2 max-md:inline-flex max-md:max-w-full max-md:items-center max-md:truncate max-md:text-caption max-md:text-muted-foreground',
    hidden: 'max-md:hidden',
};
const PHONE_SEPARATOR = "max-md:before:mr-2 max-md:before:content-['·']";
/**
 * The component technicians live in. TanStack Table with sortable headers,
 * client filters, server pagination, row selection with a per-row `disabled`
 * predicate, expandable rows, a custom row renderer for dnd-kit, a sticky
 * `micro` uppercase header at 32 px, its own empty state and density-aware
 * cells (40 / 32 px rows; 12×10 / 8×7 px padding, from the tokens).
 *
 * The header sticks inside the nearest bounded scroll container: give the
 * table `min-h-0 flex-1` in a column shell, or a `max-h-*` of its own.
 */
export function DataTable({ columns, data, getRowId, loading = false, skeletonRows = 8, emptyState, noResultsState, sorting: sortingProp, onSortingChange, defaultSorting = [], manualSorting = false, globalFilter = '', columnFilters, pagination, paginate = true, pageSizes = DEFAULT_PAGE_SIZES, defaultPageSize = 25, minRowsBeforePager = 12, selection, renderSelectionBar, expandable, expanded: expandedProp, onExpandedChange, rowRenderer, onRowClick, rowClassName, toolbar, stickyHeader = true, className, tableClassName, onTableReady, ...aria }) {
    const { t } = useTranslation();
    const [internalSorting, setInternalSorting] = React.useState(defaultSorting);
    const [internalExpanded, setInternalExpanded] = React.useState({});
    const [clientPagination, setClientPagination] = React.useState({ pageIndex: 0, pageSize: defaultPageSize });
    const sorting = sortingProp ?? internalSorting;
    const expanded = expandedProp ?? internalExpanded;
    const serverMode = pagination != null;
    const leadingColumns = React.useMemo(() => {
        const extra = [];
        if (selection) {
            extra.push({
                id: '__select',
                enableSorting: false,
                meta: { width: 36, className: 'pr-0', headerClassName: 'pr-0' },
                header: ({ table }) => {
                    const rows = table.getRowModel().rows.filter((row) => row.getCanSelect());
                    const selectedCount = rows.filter((row) => row.getIsSelected()).length;
                    const all = rows.length > 0 && selectedCount === rows.length;
                    return (_jsx(Checkbox, { "aria-label": t('kit.selectAll'), checked: all, indeterminate: selectedCount > 0 && !all, disabled: rows.length === 0, onChange: (event) => {
                            const next = { ...table.getState().rowSelection };
                            for (const row of rows) {
                                if (event.target.checked)
                                    next[row.id] = true;
                                else
                                    delete next[row.id];
                            }
                            table.setRowSelection(next);
                        } }));
                },
                cell: ({ row }) => (_jsx(Checkbox, { "aria-label": t('kit.selectRow'), checked: row.getIsSelected(), disabled: !row.getCanSelect(), onClick: (event) => event.stopPropagation(), onChange: row.getToggleSelectedHandler() })),
            });
        }
        if (expandable && expandable.toggleColumn !== false) {
            extra.push({
                id: '__expand',
                enableSorting: false,
                meta: { width: 32, className: 'pr-0', headerClassName: 'pr-0' },
                header: () => null,
                cell: ({ row }) => row.getCanExpand() ? (_jsx(Button, { variant: "ghost", size: "icon-sm", "aria-expanded": row.getIsExpanded(), "aria-label": row.getIsExpanded() ? t('kit.collapseRow') : t('kit.expandRow'), onClick: (event) => {
                        event.stopPropagation();
                        row.toggleExpanded();
                    }, children: _jsx(ChevronRight, { className: cn('transition-transform duration-(--duration-fast)', row.getIsExpanded() && 'rotate-90') }) })) : null,
            });
        }
        return extra;
    }, [selection, expandable, t]);
    const allColumns = React.useMemo(() => [...leadingColumns, ...columns], [leadingColumns, columns]);
    // TanStack Table 8 returns functions the React Compiler cannot memoize; there is no
    // compatible call pattern before @tanstack/react-table 9, which is after the release.
    // eslint-disable-next-line react-hooks/incompatible-library
    const table = useReactTable({
        data,
        columns: allColumns,
        getRowId,
        state: {
            sorting,
            globalFilter,
            columnFilters: columnFilters ?? [],
            rowSelection: selection?.selected ?? {},
            expanded,
            pagination: serverMode
                ? { pageIndex: pagination.page - 1, pageSize: pagination.pageSize }
                : clientPagination,
        },
        manualSorting,
        sortDescFirst: false,
        manualPagination: serverMode || !paginate,
        pageCount: serverMode ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : undefined,
        enableRowSelection: selection ? (row) => !selection.isRowDisabled?.(row.original) : false,
        enableExpanding: !!expandable,
        getRowCanExpand: expandable ? (row) => expandable.canExpand?.(row.original) ?? true : undefined,
        onSortingChange: onSortingChange ?? setInternalSorting,
        onExpandedChange: onExpandedChange ?? setInternalExpanded,
        onRowSelectionChange: (updater) => {
            if (!selection)
                return;
            const next = typeof updater === 'function' ? updater(selection.selected) : updater;
            selection.onChange(next);
        },
        onPaginationChange: (updater) => {
            if (serverMode) {
                const current = { pageIndex: pagination.page - 1, pageSize: pagination.pageSize };
                const next = typeof updater === 'function' ? updater(current) : updater;
                pagination.onChange({ page: next.pageIndex + 1, pageSize: next.pageSize });
            }
            else {
                setClientPagination((current) => (typeof updater === 'function' ? updater(current) : updater));
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: paginate && !serverMode ? getPaginationRowModel() : undefined,
        autoResetPageIndex: false,
    });
    React.useEffect(() => {
        onTableReady?.(table);
    }, [table, onTableReady]);
    // A filter that hides the current page's rows sends the reader back to page 1.
    const filteredCount = table.getFilteredRowModel().rows.length;
    React.useEffect(() => {
        if (serverMode)
            return;
        const pageCount = Math.max(1, Math.ceil(filteredCount / clientPagination.pageSize));
        if (clientPagination.pageIndex >= pageCount) {
            setClientPagination((current) => ({ ...current, pageIndex: 0 }));
        }
    }, [filteredCount, clientPagination.pageIndex, clientPagination.pageSize, serverMode]);
    const rows = table.getRowModel().rows;
    const visibleColumnCount = table.getVisibleLeafColumns().length;
    const total = serverMode ? pagination.total : data.length;
    const count = serverMode ? pagination.total : filteredCount;
    const showPager = !loading && (serverMode ? pagination.total > minRowsBeforePager : paginate && filteredCount > minRowsBeforePager);
    const selectedRows = selection ? table.getSelectedRowModel().flatRows.map((row) => row.original) : [];
    const pagerState = serverMode
        ? { page: pagination.page, pageSize: pagination.pageSize }
        : { page: clientPagination.pageIndex + 1, pageSize: clientPagination.pageSize };
    const from = count === 0 ? 0 : (pagerState.page - 1) * pagerState.pageSize + 1;
    const to = Math.min(pagerState.page * pagerState.pageSize, count);
    const roles = phoneRoles(table.getVisibleLeafColumns().map((column) => ({
        id: column.id,
        phone: column.columnDef.meta?.phone,
        accessor: column.accessorFn != null,
    })));
    const head = (_jsx("thead", { className: cn(stickyHeader && 'sticky top-0 z-10', 'max-md:sr-only'), children: table.getHeaderGroups().map((headerGroup) => (_jsx("tr", { children: headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const sorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                return (_jsx("th", { scope: "col", "aria-sort": canSort ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined, style: meta?.width != null ? { width: meta.width } : undefined, className: cn('h-table-header whitespace-nowrap border-b border-border bg-card px-cell-x text-micro font-semibold uppercase text-muted-foreground', alignClass(meta?.align), meta?.headerClassName), children: header.isPlaceholder ? null : canSort ? (_jsxs("button", { type: "button", className: "inline-flex items-center gap-1 rounded-sm uppercase hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", onClick: header.column.getToggleSortingHandler(), children: [flexRender(header.column.columnDef.header, header.getContext()), sorted === 'asc' ? (_jsx(ChevronUp, { className: "size-3", "aria-hidden": "true" })) : sorted === 'desc' ? (_jsx(ChevronDown, { className: "size-3", "aria-hidden": "true" })) : (_jsx(ChevronsUpDown, { className: "size-3 opacity-50", "aria-hidden": "true" }))] })) : (flexRender(header.column.columnDef.header, header.getContext())) }, header.id));
            }) }, headerGroup.id))) }));
    const renderRow = (row) => {
        const disabled = selection?.isRowDisabled?.(row.original) ?? false;
        let secondaryIndex = 0;
        const cells = row.getVisibleCells().map((cell) => {
            const meta = cell.column.columnDef.meta;
            let role = roles.get(cell.column.id) ?? 'secondary';
            // An accessor column with no value would put a lone "—" on line two.
            if (role === 'secondary' && cell.column.accessorFn) {
                const value = cell.getValue();
                if (value == null || value === '')
                    role = 'hidden';
            }
            const separated = role === 'secondary' && secondaryIndex++ > 0;
            return (_jsx("td", { className: cn('h-row border-b border-border px-cell-x py-cell-y align-middle', meta?.wrap ? 'whitespace-normal' : 'whitespace-nowrap', meta?.mono && 'text-code', meta?.dim && 'text-muted-foreground', alignClass(meta?.align), meta?.className, 'max-md:h-auto max-md:border-b-0 max-md:p-0 max-md:text-left', PHONE_CELL[role], separated && PHONE_SEPARATOR), children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id));
        });
        const className = cn('group/row transition-colors duration-(--duration-fast)', 'hover:[&>td]:bg-accent/45 data-[state=selected]:[&>td]:bg-primary-soft data-[expanded=true]:[&>td]:border-b-0 data-[expanded=true]:[&>td]:bg-muted', "max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-x-2 max-md:gap-y-0.5 max-md:border-b max-md:border-border max-md:px-3 max-md:py-2 max-md:before:order-1 max-md:before:basis-full max-md:before:content-['']", 'max-md:hover:[&>td]:bg-transparent max-md:hover:bg-accent/45 max-md:data-[state=selected]:bg-primary-soft max-md:data-[expanded=true]:bg-muted max-md:data-[expanded=true]:border-b-0', disabled && 'text-muted-foreground', onRowClick && 'cursor-pointer', rowClassName?.(row.original));
        const attrs = {
            'data-state': row.getIsSelected() ? 'selected' : undefined,
            'data-expanded': row.getIsExpanded() ? 'true' : undefined,
            'data-disabled': disabled ? 'true' : undefined,
            onClick: onRowClick ? () => onRowClick(row.original) : undefined,
            tabIndex: onRowClick ? 0 : undefined,
            onKeyDown: onRowClick
                ? (event) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onRowClick(row.original);
                    }
                }
                : undefined,
        };
        const tr = rowRenderer ? (_jsx(React.Fragment, { children: rowRenderer({ row, children: cells, className, attrs }) }, row.id)) : (_jsx("tr", { className: className, ...attrs, children: cells }, row.id));
        if (!expandable || !row.getIsExpanded())
            return tr;
        return (_jsxs(React.Fragment, { children: [tr, _jsx("tr", { "data-slot": "expanded-row", className: "max-md:block", children: _jsx("td", { colSpan: visibleColumnCount, className: "border-b border-border bg-muted p-0 max-md:block", children: expandable.renderExpanded(row.original) }) })] }, row.id));
    };
    const body = loading ? (_jsx("tbody", { "aria-busy": "true", children: Array.from({ length: skeletonRows }, (_, index) => (_jsx("tr", { children: Array.from({ length: visibleColumnCount }, (_, column) => (_jsx("td", { className: "h-row border-b border-border px-cell-x py-cell-y", children: _jsx(Skeleton, { className: cn('h-3', column === 0 ? 'w-2/3' : 'w-1/2') }) }, column))) }, index))) })) : rows.length === 0 ? (_jsx("tbody", { children: _jsx("tr", { children: _jsx("td", { colSpan: visibleColumnCount, className: "p-0", children: data.length === 0
                    ? (emptyState ?? _jsx(EmptyState, { title: t('kit.emptyTitle') }))
                    : (noResultsState ?? _jsx(EmptyState, { variant: "no-results", title: t('kit.noResultsTitle') })) }) }) })) : (_jsx("tbody", { children: rows.map(renderRow) }));
    const toolbarNode = selection && renderSelectionBar && selectedRows.length > 0
        ? renderSelectionBar({ rows: selectedRows, count: selectedRows.length, clear: () => selection.onChange({}) })
        : typeof toolbar === 'function'
            ? toolbar({ count, total })
            : toolbar;
    return (_jsxs("div", { "data-slot": "data-table", className: cn('flex min-h-0 flex-col gap-3', className), children: [toolbarNode != null && _jsx("div", { className: "shrink-0", children: toolbarNode }), _jsxs("div", { className: "flex min-h-0 flex-1 flex-col overflow-auto rounded-lg border border-border bg-card", children: [_jsxs("table", { className: cn('w-full border-collapse text-ui compact:text-body max-md:block max-md:[&>tbody]:block', tableClassName), "aria-busy": loading || undefined, ...aria, children: [head, body] }), showPager && (_jsxs("div", { className: "sticky bottom-0 mt-auto flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-3 py-2 text-caption text-muted-foreground", children: [_jsx("span", { className: "tnum", children: t('kit.pageRange', { from, to, total: count }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { id: "data-table-page-size", className: "whitespace-nowrap", children: t('kit.rowsPerPage') }), _jsx(SimpleSelect, { "aria-labelledby": "data-table-page-size", className: "h-control-sm w-auto min-w-[4.5rem] text-caption", options: pageSizes.map((size) => ({ value: String(size), label: String(size) })), value: String(pagerState.pageSize), onChange: (next) => table.setPagination({ pageIndex: 0, pageSize: Number(next) }) }), _jsx(Button, { variant: "outline", size: "icon-sm", "aria-label": t('kit.previousPage'), disabled: pagerState.page <= 1, onClick: () => table.previousPage(), children: _jsx(ChevronLeft, {}) }), _jsx(Button, { variant: "outline", size: "icon-sm", "aria-label": t('kit.nextPage'), disabled: to >= count, onClick: () => table.nextPage(), children: _jsx(ChevronRight, {}) })] })] }))] })] }));
}
export { createColumnHelper, flexRender } from '@tanstack/react-table';
//# sourceMappingURL=data-table.js.map